import type { JWTClaims } from '@scrolldex/shared';
import { ref, type Ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';

// Interface for active subscription information
export interface ActiveSubscription {
  productName: string;
  currentPeriodEnd: Date;
  status: string;
  isGifted: boolean;
  // Subscription created under Stripe Managed Payments (Link is merchant of
  // record — receipts/refunds/disputes handled by Stripe, not us).
  managedPayments: boolean;
}

// Interface for admin user data with subscription
export interface AdminUser {
  id: string;
  // Null for Discord-registered accounts whose profile had no email
  email: string | null;
  emailVerified: boolean;
  username: string;
  isAdmin: boolean;
  loginMethod: string;
  createdAt: Date;
  deletedAt: Date | null;
  deleted: boolean;
  isSupporter: boolean;
  studioAccess: boolean;
  bugReportsBlocked: boolean;
  premiumUntil: Date | null;
  stripeCustomerId: string | null;
  adminNotes: string;
  awards: string[];
  listCount: number;
  gameCount: number;
  activeSubscription: ActiveSubscription | null;
}

export type AdminUserSortColumn = 'createdAt' | 'username' | 'email' | 'premium';

// Query for GET /admin/users; every field optional (server applies defaults)
export interface AdminUsersQuery {
  page?: number; // 1-based
  pageSize?: number;
  search?: string;
  sort?: AdminUserSortColumn;
  dir?: 'asc' | 'desc';
}

export interface AdminUsersPage {
  users: AdminUser[];
  total: number; // rows matching the search across all pages
  page: number;
  pageSize: number;
}

// GET /admin/users/stats — global counts, independent of search/paging
export interface AdminUserStats {
  totalUsers: number; // non-deleted
  deletedUsers: number;
  premiumUsers: number;
  giftedPremiumUsers: number;
}

// One-time premium award code (tournament prizes etc.)
export interface RedemptionCode {
  id: string;
  code: string;
  months: number;
  note: string;
  expiresAt: Date | null;
  createdAt: Date;
  redeemedAt: Date | null;
  redeemedBy: { id: string; username: string; email: string | null } | null;
}

// Interface for server manifest data
export interface ManifestEntry {
  name: string;
  type: number; // ManifestType enum (0 = Branch, 1 = Release)
  sha: string;
  serverVersion: string;
}

export interface ServerManifest {
  [key: string]: ManifestEntry;
}

// Interface for daily count data
export interface DailyCount {
  date: number;
  count: number;
}

export interface TopArmyListCount {
  faction: string;
  count: number;
}

export interface TopEventActionCount {
  action: string;
  count: number;
}

export interface TopPathToGloryBattlepackCount {
  battlePack: string;
  count: number;
}

// Interface for admin overview data
export interface AdminOverview {
  totalUsers: number;
  premiumUsers: number;
  giftedPremiumUsers: number;
  // Devices tracked without an account — see server routes/anon.ts.
  totalAnonUsers: number;
  totalLists: number;
  deletedLists: number;
  totalCollectionLists: number;
  collectionListsLast30Days: number;
  totalPathToGloryBattlepackLists: number;
  pathToGloryBattlepackListsLast30Days: number;
  totalGames: number;
  deletedGames: number;
  totalEventLogs: number;
  listCountsByDay: DailyCount[];
  gameCountsByDay: DailyCount[];
  eventLogCountsByDay: DailyCount[];
  previousListCountsByDay: DailyCount[];
  previousGameCountsByDay: DailyCount[];
  previousEventLogCountsByDay: DailyCount[];
  factionUsageLast30Days: TopArmyListCount[];
  factionUsagePrevious30Days: TopArmyListCount[];
  topEventActionsLast30Days: TopEventActionCount[];
  topPathToGloryBattlepacks: TopPathToGloryBattlepackCount[];
}

// Distinct accounts + anonymous devices by last-seen X-App-Version header.
// `version: null` = an account that never synced at all (accounts only —
// anonymous devices have no "never pinged" bucket, since a row only exists
// once pinged); `version: 'unversioned'` = synced/pinged, but on a client
// that sent no version header.
export interface ClientVersionCount {
  version: string | null;
  accountUsers: number;
  anonUsers: number;
}

export interface ClientVersions {
  versions: ClientVersionCount[];
}

// Email reporting (ZeptoMail) — aggregate stats for the overview graph
export interface EmailStatsDay {
  date: string; // YYYY-MM-DD
  sent: number;
  delivered: number;
  hardBounce: number;
  softBounce: number;
}

export interface EmailStats {
  windowDays: number;
  totals: { sent: number; delivered: number; hardBounce: number; softBounce: number };
  byDay: EmailStatsDay[];
}

// Email reporting — per-user activity for the user detail Emails tab
export type EmailDeliveryStatus = 'delivered' | 'hard_bounce' | 'soft_bounce' | 'sent';

export interface UserEmailEntry {
  subject: string;
  templateName: string | null;
  sentAt: string | null;
  status: EmailDeliveryStatus;
  emailReference: string | null;
}

// Interface for database size data
export interface DatabaseTableInfo {
  tableName: string;
  sizeBytes: number;
}

export interface DatabaseInfo {
  totalAllocatedBytes: number;
  usedBytes: number;
  availableBytes: number;
  usagePercentage: number;
  tableBreakdown: DatabaseTableInfo[];
}

// Interface for a self-hosted bug report (admin viewer)
export interface BugReport {
  id: string;
  // Friendly title derived from type + metadata (matches the Discord thread name).
  name: string;
  type: string;
  text: string;
  userId: string | null;
  metadata: Record<string, unknown>;
  createdAt: Date;
}

// Interface for admin notes
export interface AdminNote {
  id: string;
  icon: string;
  title: string;
  content: string;
  isLink: boolean;
  page: string;
  createdAt: Date;
  updatedAt: Date;
}

// Helper to get configuration from window object or fallback to build-time constants
function getConfig() {
  // In development, use localhost. In production build, use the production API.
  const isDev = import.meta.env.DEV;
  return {
    apiUrl: isDev ? 'http://localhost:3000' : 'https://api.sigdex.io',
  };
}

const ACCESS_TOKEN_KEY = 'admin_access_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';

export const accessToken = useLocalStorage<string | null>(ACCESS_TOKEN_KEY, null);
export const claims = ref<JWTClaims | null>(null);
export const isAuthenticated = ref<boolean>(false);
export const isAdmin = ref<boolean>(false);

// Helper to decode base64 with proper UTF-8 support
function base64UrlDecode(base64Url: string): string {
  // Convert base64url to base64
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  // Decode using TextDecoder for proper UTF-8 support
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new TextDecoder('utf-8').decode(bytes);
}

// Helper to decode JWT and extract claims
function parseClaims(token: string | null): JWTClaims | null {
  if (!token) return null;
  try {
    const [, payloadBase64] = token.split('.');
    const payloadJson = base64UrlDecode(payloadBase64);
    const payload = JSON.parse(payloadJson) as JWTClaims;
    return payload;
  } catch {
    return null;
  }
}

export class AdminClient {
  private apiBaseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    const config = getConfig();
    this.apiBaseUrl = config.apiUrl.replace(/\/$/, '');
    this.loadTokens(); // Initialize authentication state from localStorage
  }

  // Decode JWT and check if expired (with optional leeway in seconds)
  private isAccessTokenExpired(leeway: number = 30): boolean {
    if (!this.accessToken) return true;
    try {
      const [, payloadBase64] = this.accessToken.split('.');
      const payload = JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp) return true;
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now + leeway;
    } catch {
      return true;
    }
  }

  // Load tokens from localStorage
  private loadTokens(): void {
    this.accessToken = accessToken.value;
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    const parsedClaims = parseClaims(this.accessToken);

    // Only set claims if user is admin
    if (parsedClaims?.isAdmin) {
      claims.value = parsedClaims;
      isAuthenticated.value = true;
      isAdmin.value = true;
    } else {
      this.clearTokens();
    }
  }

  // Save tokens to localStorage (only if admin)
  private saveTokens(newAccessToken: string, newRefreshToken: string): void {
    const parsedClaims = parseClaims(newAccessToken);

    if (!parsedClaims?.isAdmin) {
      // User is not admin, don't save tokens
      this.clearTokens();
      throw new Error('Access denied: Admin privileges required');
    }

    this.accessToken = newAccessToken;
    this.refreshToken = newRefreshToken;
    accessToken.value = newAccessToken;
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

    claims.value = parsedClaims;
    isAuthenticated.value = true;
    isAdmin.value = true;
  }

  // Remove tokens from localStorage
  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    accessToken.value = null;
    localStorage.removeItem(REFRESH_TOKEN_KEY);

    claims.value = null;
    isAuthenticated.value = false;
    isAdmin.value = false;
  }

  // Helper for fetch with auth
  async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    if (!options.headers) options.headers = {};
    const headers = options.headers as Record<string, string>;

    // Proactively refresh token if expired (with 30s leeway)
    if (this.isAccessTokenExpired() && this.refreshToken) {
      await this.refreshTokens();
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let res = await fetch(url, options);
    if (res.status === 401 && this.refreshToken) {
      // Try to refresh token again if server says unauthorized
      const refreshed = await this.refreshTokens();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        res = await fetch(url, options);
      } else {
        this.clearTokens();
      }
    }
    return res;
  }

  // Login (only allows admin users)
  async login(emailOrUsername: string, password: string): Promise<boolean> {
    const res = await fetch(`${this.apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailOrusername: emailOrUsername, password }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText);
    }

    const { accessToken, refreshToken } = await res.json();

    // This will throw an error if user is not admin
    this.saveTokens(accessToken, refreshToken);
    return true;
  }

  // Refresh tokens
  async refreshTokens(): Promise<boolean> {
    const res = await fetch(`${this.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: this.refreshToken }),
    });

    if (!res.ok) {
      this.clearTokens();
      return false;
    }

    const { accessToken, refreshToken } = await res.json();

    try {
      this.saveTokens(accessToken, refreshToken);
      return true;
    } catch {
      // User lost admin privileges
      this.clearTokens();
      return false;
    }
  }

  logout(): void {
    this.clearTokens();
  }

  // Get one page of users for the admin users table. Search / sort / paging
  // run server-side so we never ship the whole user table to the browser.
  async getUsers(params: AdminUsersQuery = {}): Promise<AdminUsersPage> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.pageSize) search.set('pageSize', String(params.pageSize));
    if (params.search) search.set('search', params.search);
    if (params.sort) search.set('sort', params.sort);
    if (params.dir) search.set('dir', params.dir);
    const qs = search.toString();

    const response = await this.fetchWithAuth(
      `${this.apiBaseUrl}/admin/users${qs ? `?${qs}` : ''}`,
    );
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    const data = await response.json();
    return {
      total: data.total,
      page: data.page,
      pageSize: data.pageSize,
      users: data.users.map((user: any) => ({
        ...user,
        createdAt: new Date(user.createdAt),
        deletedAt: user.deletedAt ? new Date(user.deletedAt) : null,
        premiumUntil: user.premiumUntil ? new Date(user.premiumUntil) : null,
        activeSubscription: user.activeSubscription
          ? {
              ...user.activeSubscription,
              currentPeriodEnd: new Date(user.activeSubscription.currentPeriodEnd),
            }
          : null,
      })),
    };
  }

  // Global user counts for the stat cards above the users table
  async getUserStats(): Promise<AdminUserStats> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/users/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch user stats');
    }
    return await response.json();
  }

  // Get overview data for admin dashboard
  async getOverview(): Promise<AdminOverview> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/overview`);
    if (!response.ok) {
      throw new Error('Failed to fetch overview data');
    }
    return await response.json();
  }

  // Get distinct-user counts grouped by last-seen client app version
  async getClientVersions(): Promise<ClientVersions> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/client-versions`);
    if (!response.ok) {
      throw new Error('Failed to fetch client version data');
    }
    return await response.json();
  }

  // Get database disk usage information
  async getDatabase(): Promise<DatabaseInfo> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/database`);
    if (!response.ok) {
      throw new Error('Failed to fetch database information');
    }
    return await response.json();
  }

  // Get bug reports (read-only viewer), optionally only those submitted by one user
  async getBugReports(userId?: string): Promise<BugReport[]> {
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/bug-reports${query}`);
    if (!response.ok) {
      throw new Error('Failed to fetch bug reports');
    }
    const data = await response.json();
    return data.bugReports.map((report: any) => ({
      ...report,
      metadata: report.metadata ?? {},
      createdAt: new Date(report.createdAt),
    }));
  }

  // List all premium redemption codes (newest first)
  async getRedemptionCodes(): Promise<RedemptionCode[]> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/redemption-codes`);
    if (!response.ok) {
      throw new Error('Failed to fetch redemption codes');
    }
    const data = await response.json();
    return data.codes.map((code: any) => ({
      ...code,
      createdAt: new Date(code.createdAt),
      expiresAt: code.expiresAt ? new Date(code.expiresAt) : null,
      redeemedAt: code.redeemedAt ? new Date(code.redeemedAt) : null,
    }));
  }

  // Bulk-generate premium redemption codes; returns the new codes
  async generateRedemptionCodes(params: {
    count: number;
    months: number;
    note?: string;
    expiresAt?: string;
  }): Promise<string[]> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/redemption-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to generate codes');
    }
    const data = await response.json();
    return data.codes;
  }

  // Revoke (delete) an unredeemed code
  async revokeRedemptionCode(id: string): Promise<void> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/redemption-codes/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to revoke code');
    }
  }

  // Get all admin notes
  async getNotes(): Promise<AdminNote[]> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/notes`);
    if (!response.ok) {
      throw new Error('Failed to fetch admin notes');
    }
    const data = await response.json();
    return data.notes.map((note: any) => ({
      ...note,
      createdAt: new Date(note.createdAt),
      updatedAt: new Date(note.updatedAt),
    }));
  }

  // Create a new admin note
  async createNote(note: {
    icon: string;
    title: string;
    content: string;
    isLink: boolean;
    page: string;
  }): Promise<AdminNote> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(note),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    const data = await response.json();
    return {
      ...data.note,
      createdAt: new Date(data.note.createdAt),
      updatedAt: new Date(data.note.updatedAt),
    };
  }

  // Update an admin note
  async updateNote(
    id: string,
    updates: {
      icon?: string;
      title?: string;
      content?: string;
      isLink?: boolean;
      page?: string;
    },
  ): Promise<AdminNote> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/notes/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    const data = await response.json();
    return {
      ...data.note,
      createdAt: new Date(data.note.createdAt),
      updatedAt: new Date(data.note.updatedAt),
    };
  }

  // Delete an admin note
  async deleteNote(id: string): Promise<{ message: string }> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/notes/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    return await response.json();
  }

  // Update user fields
  async updateUser(
    userId: string,
    updates: {
      username?: string;
      email?: string;
      emailVerified?: boolean;
      isSupporter?: boolean;
      studioAccess?: boolean;
      bugReportsBlocked?: boolean;
      deleted?: boolean;
      premiumUntil?: Date | null;
      adminNotes?: string;
      awards?: string[];
    },
  ): Promise<AdminUser> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...updates,
        premiumUntil: updates.premiumUntil ? updates.premiumUntil.toISOString() : null,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    const data = await response.json();
    return {
      ...data.user,
      createdAt: new Date(data.user.createdAt),
      deletedAt: data.user.deletedAt ? new Date(data.user.deletedAt) : null,
      premiumUntil: data.user.premiumUntil ? new Date(data.user.premiumUntil) : null,
      activeSubscription: data.user.activeSubscription
        ? {
            ...data.user.activeSubscription,
            currentPeriodEnd: new Date(data.user.activeSubscription.currentPeriodEnd),
          }
        : null,
    };
  }

  // Issue a password reset for a user (emails a reset link, returns the link too)
  async resetUserPassword(userId: string): Promise<{
    message: string;
    emailSent: boolean;
    email: string;
    resetLink: string;
  }> {
    const response = await this.fetchWithAuth(
      `${this.apiBaseUrl}/admin/users/${userId}/reset-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    return await response.json();
  }

  // Email delivery stats for the overview graph. Returns null if reporting
  // isn't configured on the server (503).
  async getEmailStats(days = 30): Promise<EmailStats | null> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/emails/stats?days=${days}`);
    if (response.status === 503) return null;
    if (!response.ok) {
      throw new Error('Failed to fetch email stats');
    }
    return await response.json();
  }

  // Emails sent to a specific user, with delivery status. Returns null if
  // reporting isn't configured on the server (503).
  async getUserEmails(userId: string): Promise<UserEmailEntry[] | null> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/users/${userId}/emails`);
    if (response.status === 503) return null;
    if (!response.ok) {
      throw new Error('Failed to fetch user emails');
    }
    const data = await response.json();
    return data.emails as UserEmailEntry[];
  }

  // Permanently delete user (only if already soft deleted)
  async deleteUser(
    userId: string,
  ): Promise<{ message: string; deletedUser: { id: string; username: string; email: string } }> {
    const response = await this.fetchWithAuth(`${this.apiBaseUrl}/admin/users/${userId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    return await response.json();
  }

  // Refresh server cache
  async refreshServerCache(): Promise<void> {
    const response = await fetch(`${this.apiBaseUrl}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key: 'sigmarpokedex', // Using the default key from server
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh server cache');
    }
  }

  // Get server manifest
  async getManifest(): Promise<ServerManifest> {
    const response = await fetch(`${this.apiBaseUrl}/manifest`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch server manifest');
    }

    return await response.json();
  }

  // Get blob data
  async getBlob(name: string = 'main'): Promise<any> {
    const response = await fetch(`${this.apiBaseUrl}/blob?name=${encodeURIComponent(name)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch blob data');
    }

    return await response.json();
  }
}

export function parseErrorMessage(error: any): string {
  try {
    const msg = JSON.parse(error.message || '{}');
    if (msg.error) {
      return msg.error;
    } else {
      return error.message || 'An unknown error occurred';
    }
  } catch {
    return error.message || 'An unknown error occurred';
  }
}

export const client: Ref<AdminClient | null> = ref(null);
